import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Plus, Trash2, Edit, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PROBLEM_ICON_OPTIONS, getProblemIcon } from '@/constants/problemSolvedIcons';

const AdmProblemsSolved = () => {
  const { portfolioData, updateProblemsSolved, addProblemSolved, deleteProblemSolved } = usePortfolio();
  const [problemsSolved, setProblemsSolved] = useState(portfolioData?.problemsSolved || []);
  const [isSaving, setIsSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [modifiedItems, setModifiedItems] = useState(new Set());

  // Update local state when portfolioData changes
  useEffect(() => {
    setProblemsSolved(portfolioData?.problemsSolved || []);
    setModifiedItems(new Set());
  }, [portfolioData]);

  const initialProblemState = {
    title: '',
    description: '',
    icon: 'Zap'
  };

  const [newProblem, setNewProblem] = useState(initialProblemState);

  const handleSave = async () => {
    if (modifiedItems.size === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateProblemsSolved(problemsSolved);
      if (result?.success) {
        setModifiedItems(new Set());
        toast.success('Problèmes résolus enregistrés avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de l\'enregistrement des problèmes résolus');
      }
    } catch (error) {
      // console.error('Error saving problems solved:', error);
      toast.error('Erreur lors de l\'enregistrement des problèmes résolus');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setProblemsSolved(portfolioData?.problemsSolved || []);
    setEditingItem(null);
    setShowAddForm(false);
    setNewProblem(initialProblemState);
    setModifiedItems(new Set());
    toast.success('Liste réinitialisée');
  };

  const handleProblemChange = (index, field, value) => {
    setProblemsSolved(prev => prev.map((problem, i) =>
      i === index ? { ...problem, [field]: value } : problem
    ));
    setModifiedItems(prev => new Set([...prev, index]));
  };

  const handleAddProblem = async () => {
    if (!newProblem.title?.trim() || !newProblem.description?.trim()) {
      toast.error('Renseignez le titre et la description');
      return;
    }

    try {
      const result = await addProblemSolved(newProblem);
      if (result?.success) {
        setProblemsSolved(prev => [...prev, result.data]);
        setNewProblem(initialProblemState);
        setShowAddForm(false);
        toast.success('Problème résolu ajouté avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de l\'ajout du problème résolu');
      }
    } catch (error) {
      // console.error('Error adding problem solved:', error);
      toast.error('Erreur lors de l\'ajout du problème résolu');
    }
  };

  const handleDeleteProblem = async (index) => {
    const problemId = problemsSolved[index]?.id ?? problemsSolved[index]?._id;
    if (!problemId) {
      toast.error('Impossible de supprimer : identifiant manquant');
      return;
    }
    try {
      const result = await deleteProblemSolved(problemId);
      if (result?.success) {
        setProblemsSolved(prev => prev.filter((_, i) => i !== index));
        toast.success('Problème résolu supprimé avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la suppression du problème résolu');
      }
    } catch (error) {
      // console.error('Error deleting problem solved:', error);
      toast.error('Erreur lors de la suppression du problème résolu');
    }
  };

  const startEditProblem = (index) => {
    setEditingItem(index);
  };

  const finishEditProblem = () => {
    setEditingItem(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Sparkles className="w-6 h-6 mr-2" />
          Problèmes Résolus
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          {problemsSolved.length} problème(s) résolu(s)
        </p>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un problème
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouveau Problème Résolu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="new-title" className="flex items-center mb-1">Titre</Label>
              <Input
                id="new-title"
                value={newProblem.title}
                onChange={(e) => setNewProblem(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Titre du problème"
              />
            </div>
            <div>
              <Label htmlFor="new-description" className="flex items-center mb-1">Description</Label>
              <Textarea
                id="new-description"
                value={newProblem.description}
                onChange={(e) => setNewProblem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="new-icon" className="flex items-center mb-1">Icône (nom Lucide)</Label>
              <Select
                value={newProblem.icon}
                onValueChange={(value) => setNewProblem((prev) => ({ ...prev, icon: value }))}
              >
                <SelectTrigger id="new-icon">
                  <SelectValue placeholder="Sélectionnez une icône" />
                </SelectTrigger>
                <SelectContent>
                  {PROBLEM_ICON_OPTIONS.map((icon) => (
                    <SelectItem key={icon} value={icon}>
                      {icon}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddProblem}>Ajouter</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {problemsSolved.map((problem, index) => {
          const ProblemIcon = getProblemIcon(problem.icon);
          const cardKey = problem.id ?? problem._id ?? index;
          return (
          <Card key={cardKey}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <ProblemIcon className="w-5 h-5 mr-2" />
                  {problem.title}
                </CardTitle>
                <div className="flex space-x-2">
                  {editingItem === index ? (
                    <Button variant="outline" size="sm" onClick={finishEditProblem}>
                      Terminer
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => startEditProblem(index)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDeleteProblem(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor={`title-${index}`} className="flex items-center mb-1">Titre</Label>
                <Input
                  id={`title-${index}`}
                  value={problem.title}
                  onChange={(e) => handleProblemChange(index, 'title', e.target.value)}
                  disabled={editingItem !== index}
                />
              </div>
              <div>
                <Label htmlFor={`description-${index}`} className="flex items-center mb-1">Description</Label>
                <Textarea
                  id={`description-${index}`}
                  value={problem.description}
                  onChange={(e) => handleProblemChange(index, 'description', e.target.value)}
                  disabled={editingItem !== index}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor={`icon-${index}`} className="flex items-center mb-1">Icône</Label>
                <Select
                  value={problem.icon || 'Zap'}
                  onValueChange={(value) => handleProblemChange(index, 'icon', value)}
                  disabled={editingItem !== index}
                >
                  <SelectTrigger id={`icon-${index}`}>
                    <SelectValue placeholder="Sélectionnez une icône" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROBLEM_ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        {icon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        );
        })}
      </div>

      {problemsSolved.length === 0 && !showAddForm && (
        <Card>
          <CardContent className="text-center py-8">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">Aucun problème résolu pour le moment</p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un problème
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdmProblemsSolved;